import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode, useRef } from 'react';
import type { User } from 'firebase/auth';
import { getReferralFromStorage } from '../utils/referralStorage';
import { setUser as setTrackingUser } from '../services/errorTracking';
import { identifyUser, cacheAnalyticsUserRole, cacheAnalyticsUserEmail } from '../utils/analytics';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    loginAsTestUser: () => void;
    logout: () => Promise<void>;
    isTestMode: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isTestMode, setIsTestMode] = useState(false);
    // Cache auth module after lazy load so logout() can reuse without re-importing
    const authRef = useRef<import('firebase/auth').Auth | null>(null);

    useEffect(() => {
        if (isTestMode) return;

        let cancelled = false;
        let unsubscribe: (() => void) | null = null;

        // Defer firebase/auth load out of the critical path. The initial render
        // starts with loading=true until onAuthStateChanged fires its first event.
        const schedule = () => {
            void (async () => {
                try {
                    const [{ onAuthStateChanged }, { auth }, oauthSyncModule] = await Promise.all([
                        import('firebase/auth'),
                        import('../firebase'),
                        import('../utils/oauthSync'),
                    ]);
                    if (cancelled) return;
                    authRef.current = auth;

                    unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
                        if (isTestMode) return;
                        setUser(currentUser);
                        setLoading(false);

                        // Persiste un flag léger lisible par le bundle marketing
                        // (qui n'importe pas Firebase). Permet aux pages publiques
                        // d'afficher un CTA "Mon compte" pour les loggés.
                        try {
                            if (currentUser) {
                                localStorage.setItem('fitd:hasSession', '1');
                            } else {
                                localStorage.removeItem('fitd:hasSession');
                            }
                        } catch { /* localStorage indispo : ignorer */ }

                        // Clear le rôle + email cachés analytics au logout ; le
                        // cas loggé est rafraîchi ci-dessous (email sync, role async).
                        if (!currentUser) {
                            cacheAnalyticsUserRole(null);
                            cacheAnalyticsUserEmail(null);
                        }

                        setTrackingUser(
                            currentUser
                                ? { id: currentUser.uid, email: currentUser.email || undefined }
                                : null
                        );

                        if (currentUser) {
                            // Cache email immédiatement (sync) pour exclure les
                            // sessions E2E @test.footinthedoor.ch dès le 1ᵉʳ event
                            // post-login, sans attendre le Firestore round-trip.
                            cacheAnalyticsUserEmail(currentUser.email ?? null);
                            identifyUser(currentUser.uid, {
                                sign_in_provider: currentUser.providerData[0]?.providerId || 'unknown',
                            });
                            try {
                                const referral = getReferralFromStorage() || undefined;
                                await oauthSyncModule.syncOAuthToFirestore(currentUser, true, referral);
                            } catch {
                                // Ne pas bloquer l'application si la synchronisation échoue
                            }
                            // Update lastSeenAt (drop-off post-signup) + cache du
                            // rôle pour exclure les sessions admin de l'analytics.
                            // Fire-and-forget.
                            void (async () => {
                                try {
                                    const [{ doc, getDoc, setDoc, serverTimestamp }, { db }] = await Promise.all([
                                        import('firebase/firestore'),
                                        import('../firebase'),
                                    ]);
                                    const userRef = doc(db, 'users', currentUser.uid);
                                    const snap = await getDoc(userRef);
                                    cacheAnalyticsUserRole((snap.data()?.role as string) ?? null);
                                    await setDoc(
                                        userRef,
                                        { lastSeenAt: serverTimestamp() },
                                        { merge: true }
                                    );
                                } catch { /* silent */ }
                            })();
                        }
                    });
                } catch (err) {
                    if (!cancelled) {
                        console.error('[Auth] failed to initialize:', err);
                        setLoading(false);
                    }
                }
            })();
        };

        const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
        if (typeof ric === 'function') {
            ric(schedule, { timeout: 1500 });
        } else {
            setTimeout(schedule, 0);
        }

        return () => {
            cancelled = true;
            if (unsubscribe) unsubscribe();
        };
    }, [isTestMode]);

    const loginAsTestUser = useCallback(() => {
        setIsTestMode(true);
        const testUser = {
            uid: 'test-user-id',
            email: 'test@example.com',
            displayName: 'Test User',
            photoURL: null,
            emailVerified: true,
            isAnonymous: false,
            metadata: {},
            providerData: [],
            refreshToken: '',
            tenantId: null,
            delete: async () => { },
            getIdToken: async () => 'mock-token',
            getIdTokenResult: async () => ({
                token: 'mock-token',
                signInProvider: 'custom',
                claims: {},
                authTime: Date.now().toString(),
                issuedAtTime: Date.now().toString(),
                expirationTime: (Date.now() + 3600000).toString(),
            }),
            reload: async () => { },
            toJSON: () => ({}),
            phoneNumber: null,
            providerId: 'custom',
        } as unknown as User;

        setUser(testUser);
        setLoading(false);
    }, []);

    const logout = useCallback(async () => {
        try { localStorage.removeItem('fitd:hasSession'); } catch { /* ignore */ }
        if (isTestMode) {
            setIsTestMode(false);
            setUser(null);
            return;
        }
        const { signOut } = await import('firebase/auth');
        if (authRef.current) {
            await signOut(authRef.current);
        } else {
            const { auth } = await import('../firebase');
            await signOut(auth);
        }
    }, [isTestMode]);

    const value = useMemo(() => ({
        user,
        loading,
        loginAsTestUser,
        logout,
        isTestMode
    }), [user, loading, isTestMode, loginAsTestUser, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
